import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RefreshCw, ArrowRight, Home, Lightbulb, BookOpen } from 'lucide-react';
import { gameBridge } from '@/services/GameBridge';
import { deckService } from '@/services/DeckService';
import { runState } from '@/services/RunState';
import { Button } from '@/components/ui/button';
import { truncate } from '@/lib/utils';
import { getSubjectByName } from '@/data/subjects';
import type { FlashCard } from '@/types/deck';

interface JudgmentData {
  card: FlashCard;
  wasCorrect: boolean;
  playerAnswer: string;
  correctAnswer: string;
  subject: string;
}

interface JudgmentUIProps {
  visible: boolean;
}

export function JudgmentUI({ visible }: JudgmentUIProps) {
  const [judgmentData, setJudgmentData] = useState<JudgmentData | null>(null);

  // Subscribe to judgment events
  useEffect(() => {
    const unsubscribe = gameBridge.subscribe('judgment-started', (data) => {
      setJudgmentData(data);
    });

    return () => unsubscribe();
  }, []);

  const handleRematch = useCallback(() => {
    if (!judgmentData) return;
    gameBridge.emit('rematch-requested', { cardId: judgmentData.card.id });
  }, [judgmentData]);

  const handleContinue = useCallback(() => {
    gameBridge.emit('continue-run', {});
  }, []);

  const handleQuitRun = useCallback(() => {
    gameBridge.emit('return-to-hub', {});
  }, []);

  if (!visible || !judgmentData) {
    return null;
  }

  const { card, subject } = judgmentData;
  const subjectInfo = getSubjectByName(subject);
  const subjectColor = subjectInfo?.color || '#4488ff';

  // Get the correct answer content
  const correctContent = card.mediumContent || card.hardContent || card.backPlain;

  // Get confusable cards
  const confusableCards = (card.confusableWith || [])
    .slice(0, 3)
    .map(id => deckService.getCardById(id))
    .filter(Boolean) as FlashCard[];

  const hasMoreCards = runState.hasMoreCards();

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-auto animate-fade-in">
      <div className="bg-game-panel/95 border-2 border-game-wrong/50 rounded-xl max-w-[640px] w-full mx-4 max-h-[90vh] overflow-y-auto animate-scale-in shadow-2xl shadow-game-wrong/20">
        {/* Header */}
        <div className="bg-gradient-to-r from-game-wrong/30 to-game-wrong/20 px-6 py-5 border-b-2 border-game-wrong/40">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-game-wrong/20 rounded-lg border border-game-wrong/40">
              <AlertTriangle className="w-6 h-6 text-game-wrong" />
            </div>
            <h2 className="text-2xl font-bold text-game-wrong">
              JUDGMENT
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-secondary/50 rounded-md font-mono text-muted-foreground">
              #{deckService.getDisplayNumber(card)}
            </span>
            <span className="text-muted-foreground/50">•</span>
            <span 
              className="px-2 py-1 rounded-md font-semibold"
              style={{ 
                color: subjectColor,
                backgroundColor: `${subjectColor}20`,
                borderColor: `${subjectColor}40`,
                border: '1px solid'
              }}
            >
              {subject}
            </span>
            <span className="text-muted-foreground/50">•</span>
            <span className="text-foreground font-medium">{card.topic}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Correct Answer Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-game-correct/20 rounded-lg">
                <BookOpen className="w-4 h-4 text-game-correct" />
              </div>
              <h3 className="text-sm font-bold text-game-correct uppercase tracking-wide">
                Correct Answer
              </h3>
            </div>
            <div className="bg-secondary/40 rounded-xl p-5 border border-game-correct/20">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {truncate(correctContent, 600)}
              </p>
            </div>
          </div>

          {/* Mnemonic Section */}
          {card.mnemonic && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-yellow-500/20 rounded-lg">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                </div>
                <h3 className="text-sm font-bold text-yellow-400 uppercase tracking-wide">
                  Remember This
                </h3>
              </div>
              <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/20">
                <p className="text-sm text-yellow-100/90 italic leading-relaxed">
                  {card.mnemonic}
                </p>
              </div>
            </div>
          )}

          {/* Confusable Cards Section */}
          {confusableCards.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Related Concepts to Review
              </h3>
              <div className="space-y-2">
                {confusableCards.map(confusable => (
                  <div
                    key={confusable.id}
                    className="text-sm text-muted-foreground bg-secondary/30 rounded-lg px-3 py-2 border border-border/40"
                  >
                    <span className="font-mono text-xs text-muted-foreground/60">
                      #{deckService.getDisplayNumber(confusable)}
                    </span>
                    <span className="mx-2 text-muted-foreground/40">•</span>
                    <span>{confusable.topic}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-5 border-t-2 border-border/60 bg-secondary/20 flex items-center justify-between gap-3">
          {/* Rematch button */}
          <Button
            variant="secondary"
            onClick={handleRematch}
            className="flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Rematch</span>
          </Button>

          {/* Continue/Return buttons */}
          <div className="flex items-center gap-3">
            {hasMoreCards ? (
              <Button
                variant="default"
                onClick={handleContinue}
                className="flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : null}

            <Button
              variant="ghost"
              onClick={handleQuitRun}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <Home className="w-4 h-4" />
              <span>Quit Run</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
